import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Checkbox,
  Typography,
  Button,
  Divider,
  InputAdornment,
  Portal,
} from '@mui/material';
import {
  Search as SearchIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
}

interface ColumnSelectorProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  open: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  onColumnsChange,
  open,
  onClose,
  anchorEl,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>(columns);
  const selectorRef = useRef<HTMLDivElement>(null);

  // Update local state when props change
  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, anchorEl]);

  const filteredColumns = localColumns.filter(column =>
    column.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleColumnToggle = (columnKey: string) => {
    const updatedColumns = localColumns.map(column =>
      column.key === columnKey
        ? { ...column, visible: !column.visible }
        : column
    );
    setLocalColumns(updatedColumns);
    // Immediately update the parent component
    onColumnsChange(updatedColumns);
  };

  const handleShowHideAll = () => {
    const allVisible = localColumns.every(column => column.visible);
    const updatedColumns = localColumns.map(column => ({
      ...column,
      visible: !allVisible
    }));
    setLocalColumns(updatedColumns);
    // Immediately update the parent component
    onColumnsChange(updatedColumns);
  };

  const handleReset = () => {
    const resetColumns = localColumns.map(column => ({
      ...column,
      visible: true // Reset to all visible
    }));
    setLocalColumns(resetColumns);
    // Immediately update the parent component
    onColumnsChange(resetColumns);
  };


  if (!open) return null;

  // Calculate position with viewport bounds checking
  const getPosition = () => {
    if (!anchorEl) return { top: 0, left: 0 };
    
    const rect = anchorEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const selectorWidth = 280;
    const selectorHeight = 400;
    
    let top = rect.bottom + 8;
    let left = rect.left;
    
    // Adjust if selector would go off the right edge
    if (left + selectorWidth > viewportWidth) {
      left = viewportWidth - selectorWidth - 16;
    }
    
    // Adjust if selector would go off the bottom edge
    if (top + selectorHeight > viewportHeight) {
      top = rect.top - selectorHeight - 8;
    }
    
    // Ensure it doesn't go off the left edge
    if (left < 16) {
      left = 16;
    }
    
    // Ensure it doesn't go off the top edge
    if (top < 16) {
      top = 16;
    }
    
    return { top, left };
  };

  const position = getPosition();

  return (
    <Portal>
      <Paper
        ref={selectorRef}
        elevation={8}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: 280,
          maxHeight: 400,
          zIndex: 1300,
          backgroundColor: 'white',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
      {/* Search Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <TextField
          fullWidth
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(0,0,0,0.6)', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              border: '1px solid #337A5C',
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              padding: '8px 12px',
              fontSize: '14px',
              fontFamily: 'Montserrat',
              color: 'rgba(0,0,0,0.87)',
              '&::placeholder': {
                color: 'rgba(0,0,0,0.6)',
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      {/* Column List */}
      <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
        {filteredColumns.map((column) => (
          <Box
            key={column.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <Checkbox
              checked={column.visible}
              onChange={() => handleColumnToggle(column.key)}
              sx={{
                color: '#337A5C',
                '&.Mui-checked': {
                  color: '#337A5C',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Montserrat',
                color: 'rgba(0,0,0,0.87)',
                fontWeight: 400,
                lineHeight: '20px',
              }}
            >
              {column.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Divider */}
      <Divider />

      {/* Footer Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
        }}
      >
        <Button
          onClick={handleShowHideAll}
          startIcon={<RemoveIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: '#337A5C',
            fontSize: '12px',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            textTransform: 'none',
            minWidth: 'auto',
            padding: '4px 8px',
            '&:hover': {
              backgroundColor: 'rgba(51, 122, 92, 0.04)',
            },
          }}
        >
          Show/Hide All
        </Button>
        <Button
          onClick={handleReset}
          sx={{
            color: 'rgba(0,0,0,0.6)',
            fontSize: '12px',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            textTransform: 'none',
            minWidth: 'auto',
            padding: '4px 8px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.04)',
            },
          }}
        >
          RESET
        </Button>
      </Box>
    </Paper>
    </Portal>
  );
};

export default ColumnSelector;
